import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
  GrowthFormSwitch,
} from '../growth_hub/GrowthModal';

const emptyForm = {
  inventory: '',
  is_enabled: true,
  bonus_type: 'PERCENTAGE',
  bonus_percentage: '20',
  bonus_fixed_amount: '0',
  min_topup_amount: '0',
  max_bonus_amount: '',
  slabs_text: '[]',
};

/**
 * Hub-wise wallet top-up bonus.
 * Example: pay ₹1000 → get ₹200 extra → bonus_percentage = 20.
 */
export function WalletTopUpBonusConfig() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    dispatch(updatepageName('wallet_topup_bonus'));
    fetchConfigs();
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubLabel = (inventoryId) => {
    const hub = inventoryList.find((h) => Number(h.value ?? h.id) === Number(inventoryId));
    if (!hub) return `Hub #${inventoryId}`;
    return hub.label || hub.place || hub.name || `Hub #${inventoryId}`;
  };

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await CustomAxios.get('wallet-topup-bonus-config/');
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.detail || 'Failed to load wallet bonus configs');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelectedId(row.id);
    setForm({
      inventory: row.inventory,
      is_enabled: Boolean(row.is_enabled),
      bonus_type: row.bonus_type || 'PERCENTAGE',
      bonus_percentage: String(row.bonus_percentage ?? '0'),
      bonus_fixed_amount: String(row.bonus_fixed_amount ?? '0'),
      min_topup_amount: String(row.min_topup_amount ?? '0'),
      max_bonus_amount: row.max_bonus_amount != null ? String(row.max_bonus_amount) : '',
      slabs_text: JSON.stringify(row.slabs || [], null, 2),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.inventory) {
      showToast('warn', 'Validation', 'Select a hub');
      return;
    }
    let slabs = [];
    try {
      slabs = JSON.parse(form.slabs_text || '[]');
      if (!Array.isArray(slabs)) throw new Error('slabs must be an array');
    } catch (e) {
      showToast('warn', 'Validation', 'Slabs must be valid JSON array');
      return;
    }

    const payload = {
      inventory: Number(form.inventory),
      is_enabled: Boolean(form.is_enabled),
      bonus_type: form.bonus_type,
      bonus_percentage: Number(form.bonus_percentage || 0),
      bonus_fixed_amount: Number(form.bonus_fixed_amount || 0),
      min_topup_amount: Number(form.min_topup_amount || 0),
      max_bonus_amount: form.max_bonus_amount === '' ? null : Number(form.max_bonus_amount),
      slabs,
    };

    try {
      setSaving(true);
      if (editMode && selectedId) {
        await CustomAxios.patch(`wallet-topup-bonus-config/${selectedId}/`, payload);
        showToast('success', 'Saved', 'Wallet bonus config updated');
      } else {
        await CustomAxios.post('wallet-topup-bonus-config/', payload);
        showToast('success', 'Created', 'Wallet bonus config created');
      }
      setModalOpen(false);
      fetchConfigs();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        JSON.stringify(err?.response?.data || {}) ||
        'Save failed';
      showToast('error', 'Error', String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      <Toast ref={toast} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5">Wallet Top-up Bonus (Hub-wise)</Typography>
          <Typography variant="body2" color="text.secondary">
            Example: pay ₹1000 → give ₹200 extra → set Percentage = 20. Optional slabs for tiers.
          </Typography>
        </Box>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add Config
        </GrowthPrimaryButton>
      </Box>

      {loading ? (
        <GrowthPageLoader label="Loading wallet bonus configs…" />
      ) : (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hub</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>%</TableCell>
              <TableCell>Fixed</TableCell>
              <TableCell>Min top-up</TableCell>
              <TableCell>Max bonus</TableCell>
              <TableCell>Slabs</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{hubLabel(row.inventory)}</TableCell>
                <TableCell>{row.is_enabled ? 'Yes' : 'No'}</TableCell>
                <TableCell>{row.bonus_type}</TableCell>
                <TableCell>{row.bonus_percentage}</TableCell>
                <TableCell>{row.bonus_fixed_amount}</TableCell>
                <TableCell>{row.min_topup_amount}</TableCell>
                <TableCell>{row.max_bonus_amount ?? '—'}</TableCell>
                <TableCell>{Array.isArray(row.slabs) ? row.slabs.length : 0}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEdit(row)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {configs.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No wallet bonus configs yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={`${editMode ? 'Edit' : 'Create'} Wallet Top-up Bonus`}
        subtitle="Bonus when users add money to Shino Wallet"
        busy={saving}
        busyLabel={editMode ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              Save
            </GrowthPrimaryButton>
          </>
        }
      >
          <GrowthFormGrid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={editMode}>
                <InputLabel>Hub</InputLabel>
                <Select
                  label="Hub"
                  value={form.inventory}
                  onChange={(e) => setForm({ ...form, inventory: e.target.value })}
                >
                  {inventoryList.map((h) => (
                    <MenuItem key={h.value ?? h.id} value={h.value ?? h.id}>
                      {h.label || h.place || h.name || `Hub #${h.value ?? h.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <GrowthFormSwitch
                label="Enabled"
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Bonus type</InputLabel>
                <Select
                  label="Bonus type"
                  value={form.bonus_type}
                  onChange={(e) => setForm({ ...form, bonus_type: e.target.value })}
                >
                  <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                  <MenuItem value="FIXED">Fixed amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Bonus %"
                value={form.bonus_percentage}
                onChange={(e) => setForm({ ...form, bonus_percentage: e.target.value })}
                helperText="20 = ₹200 free on ₹1000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Fixed bonus ₹"
                value={form.bonus_fixed_amount}
                onChange={(e) => setForm({ ...form, bonus_fixed_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Min top-up ₹"
                value={form.min_topup_amount}
                onChange={(e) => setForm({ ...form, min_topup_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Max bonus cap ₹ (optional)"
                value={form.max_bonus_amount}
                onChange={(e) => setForm({ ...form, max_bonus_amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={4}
                label="Slabs JSON (optional)"
                value={form.slabs_text}
                onChange={(e) => setForm({ ...form, slabs_text: e.target.value })}
                helperText='e.g. [{"min_amount":500,"bonus_percent":10},{"min_amount":1000,"bonus_percent":20}]'
              />
            </Grid>
          </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default WalletTopUpBonusConfig;
